/**
 * Consumer Domain Seed Data
 *
 * Contains all seed data for Consumers (Borrowers) and Partners:
 * - Consumers/Borrowers (Case Management Service 29)
 * - Partners (Case Management Service 29)
 */

import { PARTNER_TYPES, PartnerType } from '../../enums/partner.enums';
import { DEFAULT_PASSWORD, seedId } from '../utils';
import { BUSINESS_IDS, REALM_IDS } from './tenants';

// Re-export PartnerType and PARTNER_TYPES for backwards compatibility with seed scripts
export { PARTNER_TYPES, PartnerType };

// =============================================================================
// CONSUMER IDs
// =============================================================================

export const CONSUMER_IDS = {
  // VIP Tier (Trust Score 150+)
  MARCUS_THOMPSON: seedId('consumer-marcus', 1),
  ROBERT_CHEN: seedId('consumer-robert', 2),

  // Trusted Tier (Trust Score 100-149)
  ALICIA_FERNANDEZ: seedId('consumer-alicia', 3),
  DAVID_WILLIAMS: seedId('consumer-david', 4),
  MARIA_SANTOS: seedId('consumer-maria', 5),

  // Established Tier (Trust Score 50-99)
  MICHAEL_BROWN: seedId('consumer-michael', 6),
  JESSICA_LEE: seedId('consumer-jessica', 7),
  CHRISTOPHER_DAVIS: seedId('consumer-chris', 8),
  AMANDA_WILSON: seedId('consumer-amanda', 9),

  // Standard Tier (Trust Score 0-49)
  JENNIFER_MARTINEZ: seedId('consumer-jennifer', 10),
  KEVIN_JOHNSON: seedId('consumer-kevin', 11),
  STEPHANIE_GARCIA: seedId('consumer-stephanie', 12),
} as const;

export type ConsumerId = (typeof CONSUMER_IDS)[keyof typeof CONSUMER_IDS];

// =============================================================================
// CONSUMER USER IDs (for Account Service - User records for consumers)
// =============================================================================

/** User IDs for consumers - separate from Consumer IDs */
export const CONSUMER_USER_IDS = {
  MARCUS_THOMPSON: seedId('user-consumer-marcus', 1),
  ROBERT_CHEN: seedId('user-consumer-robert', 2),
  ALICIA_FERNANDEZ: seedId('user-consumer-alicia', 3),
  DAVID_WILLIAMS: seedId('user-consumer-david', 4),
  MARIA_SANTOS: seedId('user-consumer-maria', 5),
  MICHAEL_BROWN: seedId('user-consumer-michael', 6),
  JESSICA_LEE: seedId('user-consumer-jessica', 7),
  CHRISTOPHER_DAVIS: seedId('user-consumer-chris', 8),
  AMANDA_WILSON: seedId('user-consumer-amanda', 9),
  JENNIFER_MARTINEZ: seedId('user-consumer-jennifer', 10),
  KEVIN_JOHNSON: seedId('user-consumer-kevin', 11),
  STEPHANIE_GARCIA: seedId('user-consumer-stephanie', 12),
} as const;

export type ConsumerUserId = (typeof CONSUMER_USER_IDS)[keyof typeof CONSUMER_USER_IDS];

// =============================================================================
// PARTNER IDs
// =============================================================================

export const PARTNER_IDS = {
  // Channel Partners (Mortgage Brokers)
  PREMIER_MORTGAGE: seedId('partner-premier', 1),
  AUSTIN_MORTGAGE: seedId('partner-austin', 2),
  FLORIDA_FIRST: seedId('partner-florida', 3),

  // Referral Partners
  CAPITAL_FUNDING: seedId('partner-capital', 4),
  RE_INVESTOR_ASSOC: seedId('partner-reinvest', 5),

  // Wholesale Partners
  PACIFIC_WHOLESALE: seedId('partner-pacific', 6),
} as const;

export type PartnerId = (typeof PARTNER_IDS)[keyof typeof PARTNER_IDS];

// =============================================================================
// PARTNER USER IDs (for Account Service - User records for partners)
// =============================================================================

/** User IDs for partners - separate from Partner IDs */
export const PARTNER_USER_IDS = {
  PREMIER_MORTGAGE: seedId('user-partner-premier', 1),
  AUSTIN_MORTGAGE: seedId('user-partner-austin', 2),
  FLORIDA_FIRST: seedId('user-partner-florida', 3),
  CAPITAL_FUNDING: seedId('user-partner-capital', 4),
  RE_INVESTOR_ASSOC: seedId('user-partner-reinvest', 5),
  PACIFIC_WHOLESALE: seedId('user-partner-pacific', 6),
} as const;

export type PartnerUserId = (typeof PARTNER_USER_IDS)[keyof typeof PARTNER_USER_IDS];

// =============================================================================
// TRUST TIERS
// =============================================================================

export const TRUST_TIERS = {
  VIP: 'vip',
  TRUSTED: 'trusted',
  ESTABLISHED: 'established',
  STANDARD: 'standard',
} as const;

export type TrustTier = (typeof TRUST_TIERS)[keyof typeof TRUST_TIERS];

// Note: PartnerType enum is imported from enums/partner.enums.ts and re-exported above

// =============================================================================
// ADDRESS DATA
// =============================================================================

export interface ConsumerAddressSeedData {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
}

// =============================================================================
// EMPLOYMENT DATA
// =============================================================================

export interface EmploymentSeedData {
  employerName?: string;
  jobTitle?: string;
  employmentType: 'employed' | 'self_employed' | 'retired' | 'unemployed' | 'business_owner';
  annualIncome?: number;
  yearsEmployed?: number;
  industryType?: string;
}

// =============================================================================
// CONSUMER DATA
// =============================================================================

export interface ConsumerSeedData {
  id: string;
  /** User ID in Account Service (24) - foreign key for authentication */
  userId: string;
  realmId: string;
  businessId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  /** Mailing address */
  address?: ConsumerAddressSeedData;
  /** Date of birth (YYYY-MM-DD format) */
  dateOfBirth?: string;
  /** Social Security Number (last 4 digits only for display) */
  ssnLast4?: string;
  /** Credit score (300-850 FICO range) */
  creditScore?: number;
  /** Employment information */
  employment?: EmploymentSeedData;
  /** Business entity name (for LLC/Corp borrowers) */
  entityName?: string;
  /** Business EIN (for LLC/Corp borrowers) */
  entityEin?: string;
  /** Trust tier: 'vip' | 'trusted' | 'established' | 'standard' */
  trustTier: string;
  /** Trust score (0-200+), determines tier and privileges */
  trustScore: number;
  /** Number of successfully completed loan deals */
  completedDeals: number;
  /** Estimated net worth in dollars (for investor qualification) */
  estimatedNetWorth?: number;
  /** Estimated liquid assets in dollars */
  estimatedLiquidity?: number;
  /** Preferred contact method: 'email' | 'phone' | 'sms' */
  preferredContact?: string;
  /** Portal type - consumers access via consumer portal */
  portal: 'admin' | 'business' | 'consumer';
  /** Investment experience in years */
  investmentExperienceYears?: number;
  /** Primary investment strategy */
  investmentStrategy?: 'fix_flip' | 'buy_hold' | 'brrr' | 'wholesale' | 'development' | 'mixed';
  /** Target markets (cities/regions) */
  targetMarkets?: string[];
}

export const CONSUMERS: Record<keyof typeof CONSUMER_IDS, ConsumerSeedData> = {
  // VIP Tier
  MARCUS_THOMPSON: {
    id: CONSUMER_IDS.MARCUS_THOMPSON,
    userId: CONSUMER_USER_IDS.MARCUS_THOMPSON,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'marcus.thompson@investorpro.com',
    firstName: 'Marcus',
    lastName: 'Thompson',
    phone: '+1-512-555-0101',
    address: {
      street: '4521 Lake Austin Boulevard',
      city: 'Austin',
      state: 'TX',
      zip: '78703',
      county: 'Travis',
    },
    dateOfBirth: '1978-03-15',
    ssnLast4: '4589',
    creditScore: 745,
    employment: {
      employerName: 'Thompson Capital Investments LLC',
      jobTitle: 'Managing Partner',
      employmentType: 'business_owner',
      annualIncome: 485000,
      yearsEmployed: 12,
      industryType: 'Real Estate Investment',
    },
    entityName: 'Thompson Capital Investments LLC',
    entityEin: '84-1234567',
    trustTier: TRUST_TIERS.VIP,
    trustScore: 165,
    completedDeals: 12,
    estimatedNetWorth: 4500000,
    estimatedLiquidity: 850000,
    preferredContact: 'sms',
    portal: 'consumer',
    investmentExperienceYears: 12,
    investmentStrategy: 'mixed',
    targetMarkets: ['Austin', 'Dallas', 'San Antonio'],
  },
  ROBERT_CHEN: {
    id: CONSUMER_IDS.ROBERT_CHEN,
    userId: CONSUMER_USER_IDS.ROBERT_CHEN,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'robert.chen@chenfamilyinvest.com',
    firstName: 'Robert',
    lastName: 'Chen',
    phone: '+1-713-555-0102',
    address: {
      street: '2847 River Oaks Boulevard',
      city: 'Houston',
      state: 'TX',
      zip: '77019',
      county: 'Harris',
    },
    dateOfBirth: '1972-08-22',
    ssnLast4: '7823',
    creditScore: 768,
    employment: {
      employerName: 'Chen Family Investments LLC',
      jobTitle: 'Principal',
      employmentType: 'business_owner',
      annualIncome: 725000,
      yearsEmployed: 18,
      industryType: 'Private Equity & Real Estate',
    },
    entityName: 'Chen Family Investments LLC',
    entityEin: '84-2345678',
    trustTier: TRUST_TIERS.VIP,
    trustScore: 180,
    completedDeals: 15,
    estimatedNetWorth: 6200000,
    estimatedLiquidity: 1200000,
    preferredContact: 'email',
    portal: 'consumer',
    investmentExperienceYears: 18,
    investmentStrategy: 'buy_hold',
    targetMarkets: ['Houston', 'Dallas', 'Fort Worth'],
  },

  // Trusted Tier
  ALICIA_FERNANDEZ: {
    id: CONSUMER_IDS.ALICIA_FERNANDEZ,
    userId: CONSUMER_USER_IDS.ALICIA_FERNANDEZ,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'alicia.fernandez@sunbeltdev.com',
    firstName: 'Alicia',
    lastName: 'Fernandez',
    phone: '+1-305-555-0103',
    address: {
      street: '1200 Brickell Avenue',
      unit: 'Unit 2405',
      city: 'Miami',
      state: 'FL',
      zip: '33131',
      county: 'Miami-Dade',
    },
    dateOfBirth: '1982-11-08',
    ssnLast4: '3456',
    creditScore: 728,
    employment: {
      employerName: 'Sunbelt Development Group LLC',
      jobTitle: 'CEO',
      employmentType: 'business_owner',
      annualIncome: 325000,
      yearsEmployed: 9,
      industryType: 'Real Estate Development',
    },
    entityName: 'Sunbelt Development Group LLC',
    entityEin: '84-3456789',
    trustTier: TRUST_TIERS.TRUSTED,
    trustScore: 125,
    completedDeals: 7,
    estimatedNetWorth: 2800000,
    estimatedLiquidity: 450000,
    preferredContact: 'phone',
    portal: 'consumer',
    investmentExperienceYears: 9,
    investmentStrategy: 'buy_hold',
    targetMarkets: ['Tampa', 'Miami', 'Orlando'],
  },
  DAVID_WILLIAMS: {
    id: CONSUMER_IDS.DAVID_WILLIAMS,
    userId: CONSUMER_USER_IDS.DAVID_WILLIAMS,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'david.williams@williamsrealty.com',
    firstName: 'David',
    lastName: 'Williams',
    phone: '+1-469-555-0104',
    address: {
      street: '5847 Preston Road',
      unit: 'Suite 200',
      city: 'Plano',
      state: 'TX',
      zip: '75093',
      county: 'Collin',
    },
    dateOfBirth: '1975-06-20',
    ssnLast4: '5678',
    creditScore: 712,
    employment: {
      employerName: 'Williams Realty Holdings LLC',
      jobTitle: 'Managing Member',
      employmentType: 'business_owner',
      annualIncome: 275000,
      yearsEmployed: 7,
      industryType: 'Real Estate Investment',
    },
    entityName: 'Williams Realty Holdings LLC',
    entityEin: '84-4567890',
    trustTier: TRUST_TIERS.TRUSTED,
    trustScore: 110,
    completedDeals: 5,
    estimatedNetWorth: 1900000,
    estimatedLiquidity: 320000,
    preferredContact: 'email',
    portal: 'consumer',
    investmentExperienceYears: 7,
    investmentStrategy: 'buy_hold',
    targetMarkets: ['Dallas', 'Plano', 'Frisco'],
  },
  MARIA_SANTOS: {
    id: CONSUMER_IDS.MARIA_SANTOS,
    userId: CONSUMER_USER_IDS.MARIA_SANTOS,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    email: 'maria.santos@santosventures.com',
    firstName: 'Maria',
    lastName: 'Santos',
    phone: '+1-210-555-0105',
    address: {
      street: '100 E Houston Street',
      unit: 'Suite 1800',
      city: 'San Antonio',
      state: 'TX',
      zip: '78205',
      county: 'Bexar',
    },
    dateOfBirth: '1980-04-12',
    ssnLast4: '6789',
    creditScore: 742,
    employment: {
      employerName: 'Santos Ventures LLC',
      jobTitle: 'Founder',
      employmentType: 'business_owner',
      annualIncome: 295000,
      yearsEmployed: 8,
      industryType: 'Real Estate Development',
    },
    entityName: 'Santos Ventures LLC',
    entityEin: '84-5678901',
    trustTier: TRUST_TIERS.TRUSTED,
    trustScore: 135,
    completedDeals: 6,
    estimatedNetWorth: 2100000,
    estimatedLiquidity: 380000,
    preferredContact: 'sms',
    portal: 'consumer',
    investmentExperienceYears: 8,
    investmentStrategy: 'development',
    targetMarkets: ['San Antonio', 'Austin', 'New Braunfels'],
  },

  // Established Tier
  MICHAEL_BROWN: {
    id: CONSUMER_IDS.MICHAEL_BROWN,
    userId: CONSUMER_USER_IDS.MICHAEL_BROWN,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'michael.brown@brownproperties.com',
    firstName: 'Michael',
    lastName: 'Brown',
    phone: '+1-832-555-0106',
    address: {
      street: '4521 Westheimer Road',
      city: 'Houston',
      state: 'TX',
      zip: '77027',
      county: 'Harris',
    },
    dateOfBirth: '1985-09-03',
    ssnLast4: '7890',
    creditScore: 705,
    employment: {
      employerName: 'Brown Properties Inc',
      jobTitle: 'President',
      employmentType: 'business_owner',
      annualIncome: 165000,
      yearsEmployed: 5,
      industryType: 'Real Estate Investment',
    },
    entityName: 'Brown Properties Inc',
    entityEin: '84-6789012',
    trustTier: TRUST_TIERS.ESTABLISHED,
    trustScore: 75,
    completedDeals: 3,
    estimatedNetWorth: 950000,
    estimatedLiquidity: 180000,
    preferredContact: 'email',
    portal: 'consumer',
    investmentExperienceYears: 5,
    investmentStrategy: 'buy_hold',
    targetMarkets: ['Houston', 'Katy', 'Sugar Land'],
  },
  JESSICA_LEE: {
    id: CONSUMER_IDS.JESSICA_LEE,
    userId: CONSUMER_USER_IDS.JESSICA_LEE,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'jessica.lee@leeinvestments.com',
    firstName: 'Jessica',
    lastName: 'Lee',
    phone: '+1-214-555-0107',
    address: {
      street: '2847 Maple Avenue',
      unit: 'Unit 512',
      city: 'Dallas',
      state: 'TX',
      zip: '75201',
      county: 'Dallas',
    },
    dateOfBirth: '1988-02-14',
    ssnLast4: '8901',
    creditScore: 698,
    employment: {
      employerName: 'Lee Investments LLC',
      jobTitle: 'Managing Partner',
      employmentType: 'business_owner',
      annualIncome: 145000,
      yearsEmployed: 3,
      industryType: 'Real Estate Investment',
    },
    entityName: 'Lee Investments LLC',
    entityEin: '84-7890123',
    trustTier: TRUST_TIERS.ESTABLISHED,
    trustScore: 65,
    completedDeals: 2,
    estimatedNetWorth: 720000,
    estimatedLiquidity: 145000,
    preferredContact: 'phone',
    portal: 'consumer',
    investmentExperienceYears: 3,
    investmentStrategy: 'fix_flip',
    targetMarkets: ['Dallas', 'Fort Worth', 'Arlington'],
  },
  CHRISTOPHER_DAVIS: {
    id: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    userId: CONSUMER_USER_IDS.CHRISTOPHER_DAVIS,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    email: 'chris.davis@davisflipping.com',
    firstName: 'Christopher',
    lastName: 'Davis',
    phone: '+1-817-555-0108',
    address: {
      street: '1892 Cedar Lane',
      city: 'Fort Worth',
      state: 'TX',
      zip: '76107',
      county: 'Tarrant',
    },
    dateOfBirth: '1983-07-28',
    ssnLast4: '9012',
    creditScore: 695,
    employment: {
      employerName: 'Davis Flipping Co LLC',
      jobTitle: 'Owner',
      employmentType: 'business_owner',
      annualIncome: 185000,
      yearsEmployed: 6,
      industryType: 'Real Estate Flipping',
    },
    entityName: 'Davis Flipping Co LLC',
    entityEin: '84-8901234',
    trustTier: TRUST_TIERS.ESTABLISHED,
    trustScore: 85,
    completedDeals: 4,
    estimatedNetWorth: 1100000,
    estimatedLiquidity: 220000,
    preferredContact: 'sms',
    portal: 'consumer',
    investmentExperienceYears: 6,
    investmentStrategy: 'fix_flip',
    targetMarkets: ['Fort Worth', 'Arlington', 'Grand Prairie'],
  },
  AMANDA_WILSON: {
    id: CONSUMER_IDS.AMANDA_WILSON,
    userId: CONSUMER_USER_IDS.AMANDA_WILSON,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'amanda.wilson@wilsonrealestate.com',
    firstName: 'Amanda',
    lastName: 'Wilson',
    phone: '+1-972-555-0109',
    address: {
      street: '6521 Preston Road',
      city: 'Dallas',
      state: 'TX',
      zip: '75205',
      county: 'Dallas',
    },
    dateOfBirth: '1990-12-05',
    ssnLast4: '0123',
    creditScore: 688,
    employment: {
      employerName: 'Wilson Real Estate Group LLC',
      jobTitle: 'Founder',
      employmentType: 'business_owner',
      annualIncome: 125000,
      yearsEmployed: 3,
      industryType: 'Real Estate Investment',
    },
    entityName: 'Wilson Real Estate Group LLC',
    entityEin: '84-9012345',
    trustTier: TRUST_TIERS.ESTABLISHED,
    trustScore: 55,
    completedDeals: 2,
    estimatedNetWorth: 680000,
    estimatedLiquidity: 125000,
    preferredContact: 'email',
    portal: 'consumer',
    investmentExperienceYears: 3,
    investmentStrategy: 'brrr',
    targetMarkets: ['Dallas', 'Richardson', 'Garland'],
  },

  // Standard Tier
  JENNIFER_MARTINEZ: {
    id: CONSUMER_IDS.JENNIFER_MARTINEZ,
    userId: CONSUMER_USER_IDS.JENNIFER_MARTINEZ,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'jennifer.martinez@gmail.com',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    phone: '+1-512-555-0110',
    address: {
      street: '4200 E Riverside Drive',
      unit: 'Apt 312',
      city: 'Austin',
      state: 'TX',
      zip: '78741',
      county: 'Travis',
    },
    dateOfBirth: '1992-05-18',
    ssnLast4: '1234',
    creditScore: 715,
    employment: {
      employerName: 'Tech Solutions Inc',
      jobTitle: 'Software Engineer',
      employmentType: 'employed',
      annualIncome: 135000,
      yearsEmployed: 4,
      industryType: 'Technology',
    },
    trustTier: TRUST_TIERS.STANDARD,
    trustScore: 25,
    completedDeals: 0,
    estimatedNetWorth: 350000,
    estimatedLiquidity: 75000,
    preferredContact: 'email',
    portal: 'consumer',
    investmentExperienceYears: 0,
    investmentStrategy: 'fix_flip',
    targetMarkets: ['Austin'],
  },
  KEVIN_JOHNSON: {
    id: CONSUMER_IDS.KEVIN_JOHNSON,
    userId: CONSUMER_USER_IDS.KEVIN_JOHNSON,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'kevin.johnson@outlook.com',
    firstName: 'Kevin',
    lastName: 'Johnson',
    phone: '+1-713-555-0111',
    address: {
      street: '8521 Memorial Drive',
      city: 'Houston',
      state: 'TX',
      zip: '77024',
      county: 'Harris',
    },
    dateOfBirth: '1987-10-22',
    ssnLast4: '2345',
    creditScore: 720,
    employment: {
      employerName: 'Houston Medical Center',
      jobTitle: 'Physical Therapist',
      employmentType: 'employed',
      annualIncome: 95000,
      yearsEmployed: 6,
      industryType: 'Healthcare',
    },
    trustTier: TRUST_TIERS.STANDARD,
    trustScore: 15,
    completedDeals: 0,
    estimatedNetWorth: 280000,
    estimatedLiquidity: 55000,
    preferredContact: 'phone',
    portal: 'consumer',
    investmentExperienceYears: 0,
    investmentStrategy: 'buy_hold',
    targetMarkets: ['Houston'],
  },
  STEPHANIE_GARCIA: {
    id: CONSUMER_IDS.STEPHANIE_GARCIA,
    userId: CONSUMER_USER_IDS.STEPHANIE_GARCIA,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    email: 'stephanie.garcia@yahoo.com',
    firstName: 'Stephanie',
    lastName: 'Garcia',
    phone: '+1-469-555-0112',
    address: {
      street: '3200 Knox Street',
      unit: 'Unit 205',
      city: 'Dallas',
      state: 'TX',
      zip: '75205',
      county: 'Dallas',
    },
    dateOfBirth: '1994-08-30',
    ssnLast4: '3456',
    creditScore: 692,
    employment: {
      employerName: 'Self',
      jobTitle: 'Real Estate Agent',
      employmentType: 'self_employed',
      annualIncome: 78000,
      yearsEmployed: 2,
      industryType: 'Real Estate Sales',
    },
    trustTier: TRUST_TIERS.STANDARD,
    trustScore: 10,
    completedDeals: 0,
    estimatedNetWorth: 220000,
    estimatedLiquidity: 42000,
    preferredContact: 'sms',
    portal: 'consumer',
    investmentExperienceYears: 0,
    investmentStrategy: 'brrr',
    targetMarkets: ['Dallas', 'Plano'],
  },
};

// =============================================================================
// PARTNER DATA
// =============================================================================

export interface PartnerSeedData {
  id: string;
  /** User ID in Account Service (24) - foreign key for authentication */
  userId: string;
  realmId: string;
  businessId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  companyName: string;
  partnerType: string;
  license?: string;
  licenseState?: string;
  /**
   * Commission rate as a percentage (e.g., 1.5 = 1.5%).
   * Stored in database as Decimal(5, 2).
   * Range: 0.00 - 999.99 (typically 0.25% - 2.0% for partners)
   */
  commissionRate: number;
  /**
   * Average number of loan deals submitted per month.
   * Used for partner tier determination and performance tracking.
   * Stored in partner metadata JSON, not a separate schema field.
   */
  monthlyVolume?: number;
  /** Total referrals submitted by this partner (all-time count) */
  totalReferrals?: number;
  /** Conversion rate as a percentage (e.g., 68 = 68% of referrals convert to loans) */
  conversionRate?: number;
  /** Portal type - partners access via consumer portal */
  portal: 'admin' | 'business' | 'consumer';
}

export const PARTNERS: Record<keyof typeof PARTNER_IDS, PartnerSeedData> = {
  // Channel Partners
  PREMIER_MORTGAGE: {
    id: PARTNER_IDS.PREMIER_MORTGAGE,
    userId: PARTNER_USER_IDS.PREMIER_MORTGAGE,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'sarah.mitchell@premiermortgage.com',
    firstName: 'Sarah',
    lastName: 'Mitchell',
    phone: '+1-305-555-0201',
    companyName: 'Premier Mortgage Solutions',
    partnerType: PartnerType.REFERRAL,
    license: 'MLO-123456',
    licenseState: 'FL',
    commissionRate: 1.5,
    monthlyVolume: 12,
    totalReferrals: 47,
    conversionRate: 68,
    portal: 'consumer',
  },
  AUSTIN_MORTGAGE: {
    id: PARTNER_IDS.AUSTIN_MORTGAGE,
    userId: PARTNER_USER_IDS.AUSTIN_MORTGAGE,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'john.smith@austinmortgage.com',
    firstName: 'John',
    lastName: 'Smith',
    phone: '+1-512-555-0202',
    companyName: 'Austin Mortgage Group',
    partnerType: PartnerType.WHITE_LABEL,
    license: 'MLO-234567',
    licenseState: 'TX',
    commissionRate: 1.25,
    monthlyVolume: 8,
    totalReferrals: 32,
    conversionRate: 62,
    portal: 'consumer',
  },
  FLORIDA_FIRST: {
    id: PARTNER_IDS.FLORIDA_FIRST,
    userId: PARTNER_USER_IDS.FLORIDA_FIRST,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    email: 'lisa.rodriguez@floridafirst.com',
    firstName: 'Lisa',
    lastName: 'Rodriguez',
    phone: '+1-813-555-0203',
    companyName: 'Florida First Lending',
    partnerType: PartnerType.WHITE_LABEL,
    license: 'MLO-345678',
    licenseState: 'FL',
    commissionRate: 1.35,
    monthlyVolume: 10,
    totalReferrals: 28,
    conversionRate: 71,
    portal: 'consumer',
  },

  // Referral Partners
  CAPITAL_FUNDING: {
    id: PARTNER_IDS.CAPITAL_FUNDING,
    userId: PARTNER_USER_IDS.CAPITAL_FUNDING,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'david.chen@capitalfunding.com',
    firstName: 'David',
    lastName: 'Chen',
    phone: '+1-832-555-0204',
    companyName: 'Capital Funding Referrals',
    partnerType: PartnerType.REFERRAL,
    commissionRate: 0.5,
    totalReferrals: 85,
    conversionRate: 45,
    portal: 'consumer',
  },
  RE_INVESTOR_ASSOC: {
    id: PARTNER_IDS.RE_INVESTOR_ASSOC,
    userId: PARTNER_USER_IDS.RE_INVESTOR_ASSOC,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    email: 'membership@reinvestorassoc.org',
    firstName: 'RE Investor',
    lastName: 'Association',
    phone: '+1-214-555-0205',
    companyName: 'RE Investor Association',
    partnerType: PartnerType.REFERRAL,
    commissionRate: 0.25,
    totalReferrals: 156,
    conversionRate: 38,
    portal: 'consumer',
  },

  // Wholesale Partners
  PACIFIC_WHOLESALE: {
    id: PARTNER_IDS.PACIFIC_WHOLESALE,
    userId: PARTNER_USER_IDS.PACIFIC_WHOLESALE,
    realmId: REALM_IDS.LOAN_SERVICING,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    email: 'wholesale@pacificwholesale.com',
    firstName: 'Pacific',
    lastName: 'Wholesale',
    phone: '+1-415-555-0206',
    companyName: 'Pacific Wholesale Lending',
    partnerType: PartnerType.WHITE_LABEL,
    license: 'CFL-456789',
    licenseState: 'CA',
    commissionRate: 0.75,
    monthlyVolume: 25,
    totalReferrals: 112,
    conversionRate: 55,
    portal: 'consumer',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all consumers as an array for seeding
 */
export function getAllConsumers(): ConsumerSeedData[] {
  return Object.values(CONSUMERS);
}

/**
 * Get consumers by trust tier
 */
export function getConsumersByTier(tier: TrustTier): ConsumerSeedData[] {
  return Object.values(CONSUMERS).filter((c) => c.trustTier === tier);
}

/**
 * Get consumers by business
 */
export function getConsumersByBusiness(businessId: string): ConsumerSeedData[] {
  return Object.values(CONSUMERS).filter((c) => c.businessId === businessId);
}

/**
 * Get all partners as an array for seeding
 */
export function getAllPartners(): PartnerSeedData[] {
  return Object.values(PARTNERS);
}

/**
 * Get partners by type
 */
export function getPartnersByType(type: PartnerType): PartnerSeedData[] {
  return Object.values(PARTNERS).filter((p) => p.partnerType === type);
}

/**
 * Get partners by business
 */
export function getPartnersByBusiness(businessId: string): PartnerSeedData[] {
  return Object.values(PARTNERS).filter((p) => p.businessId === businessId);
}

/**
 * Get consumer login credentials
 */
export function getConsumerCredentials(consumerId: keyof typeof CONSUMER_IDS): { email: string; password: string } {
  const consumer = CONSUMERS[consumerId];
  return {
    email: consumer.email,
    password: DEFAULT_PASSWORD,
  };
}

/**
 * Get partner login credentials
 */
export function getPartnerCredentials(partnerId: keyof typeof PARTNER_IDS): { email: string; password: string } {
  const partner = PARTNERS[partnerId];
  return {
    email: partner.email,
    password: DEFAULT_PASSWORD,
  };
}

// =============================================================================
// E2E TEST SEED IDs (Issue #281 - Consumer CRUD E2E scenarios)
// =============================================================================

/**
 * E2E Consumer Seed IDs for canary test scenarios
 * These IDs are used by the Control Tower E2E scenario executor
 * @see Issue #281 - Consumer CRUD E2E scenarios
 */
export const E2E_CONSUMER_SEED_IDS = {
  // Consumers with different states for status transition testing
  /** Active consumer for testing operations on active consumers */
  CONSUMER_ACTIVE: CONSUMER_IDS.MARCUS_THOMPSON,
  /** Consumer to be used for pending→active transition tests */
  CONSUMER_PENDING: seedId('e2e-consumer-pending', 1),
  /** Consumer to be used for suspension tests */
  CONSUMER_SUSPENDED: seedId('e2e-consumer-suspended', 2),

  // Invites for testing invite flows
  /** Pending invite for resend testing */
  INVITE_PENDING: seedId('e2e-invite-pending', 1),
  /** Expired invite for error handling tests */
  INVITE_EXPIRED: seedId('e2e-invite-expired', 2),

  // Magic links for validation testing
  /** Valid magic link token for positive test cases */
  MAGIC_LINK_VALID: 'ml_test_valid_token_12345',
  /** Expired magic link token for negative test cases */
  MAGIC_LINK_EXPIRED: 'ml_test_expired_token_12345',
  /** Already-used magic link token for single-use validation */
  MAGIC_LINK_USED: 'ml_test_used_token_12345',
} as const;

export type E2EConsumerSeedId = (typeof E2E_CONSUMER_SEED_IDS)[keyof typeof E2E_CONSUMER_SEED_IDS];

/**
 * E2E Consumer Seed Data for different states
 * @see Issue #281 - Consumer CRUD E2E scenarios
 */
export const E2E_CONSUMERS = {
  /** Pending consumer for activation tests */
  PENDING: {
    id: E2E_CONSUMER_SEED_IDS.CONSUMER_PENDING,
    email: 'e2e-consumer-pending@test.com',
    firstName: 'E2E',
    lastName: 'Pending',
    status: 'pending' as const,
    tier: 'standard' as const,
  },
  /** Suspended consumer for reactivation tests */
  SUSPENDED: {
    id: E2E_CONSUMER_SEED_IDS.CONSUMER_SUSPENDED,
    email: 'e2e-consumer-suspended@test.com',
    firstName: 'E2E',
    lastName: 'Suspended',
    status: 'suspended' as const,
    tier: 'standard' as const,
  },
} as const;

// =============================================================================
// CO-BORROWER RELATIONSHIPS (Issue #469 - Seed Data Gap Analysis)
// =============================================================================

/**
 * Co-borrower relationship types
 * Matches the enum in co-borrower-invite.dto.ts
 */
export const CO_BORROWER_RELATIONSHIPS = {
  SPOUSE: 'spouse',
  BUSINESS_PARTNER: 'business_partner',
  GUARANTOR: 'guarantor',
  OTHER: 'other',
} as const;

export type CoBorrowerRelationship = (typeof CO_BORROWER_RELATIONSHIPS)[keyof typeof CO_BORROWER_RELATIONSHIPS];

/**
 * Co-borrower relationship mapping
 * Maps primary borrower consumer ID to their co-borrower(s)
 * Used by case and deal seeders to add realistic co-borrower data
 */
export interface CoBorrowerMapping {
  primaryConsumerId: string;
  coBorrowerId: string;
  relationship: CoBorrowerRelationship;
  /** Email of the co-borrower (for case metadata) */
  email: string;
  /** Full name of the co-borrower */
  name: string;
}

/**
 * Predefined co-borrower relationships for seed data
 * These represent realistic spouse/partner co-borrower scenarios in lending
 */
export const CO_BORROWER_MAPPINGS: CoBorrowerMapping[] = [
  // Marcus Thompson's spouse is a silent co-borrower on his investments
  {
    primaryConsumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    coBorrowerId: CONSUMER_IDS.AMANDA_WILSON, // Using Amanda as co-borrower for demo
    relationship: CO_BORROWER_RELATIONSHIPS.SPOUSE,
    email: 'sarah.thompson@investorpro.com',
    name: 'Sarah Thompson',
  },
  // Robert Chen has a business partner co-borrower
  {
    primaryConsumerId: CONSUMER_IDS.ROBERT_CHEN,
    coBorrowerId: CONSUMER_IDS.DAVID_WILLIAMS,
    relationship: CO_BORROWER_RELATIONSHIPS.BUSINESS_PARTNER,
    email: 'lisa.chen@chenfamilyinvest.com',
    name: 'Lisa Chen',
  },
  // Jessica Lee has a guarantor on her fix-and-flip loans
  {
    primaryConsumerId: CONSUMER_IDS.JESSICA_LEE,
    coBorrowerId: CONSUMER_IDS.MICHAEL_BROWN,
    relationship: CO_BORROWER_RELATIONSHIPS.GUARANTOR,
    email: 'james.lee@leeinvestments.com',
    name: 'James Lee',
  },
  // Maria Santos has a business partner for multi-family deals
  {
    primaryConsumerId: CONSUMER_IDS.MARIA_SANTOS,
    coBorrowerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    relationship: CO_BORROWER_RELATIONSHIPS.BUSINESS_PARTNER,
    email: 'carlos.santos@santosventures.com',
    name: 'Carlos Santos',
  },
];

/**
 * Get co-borrower mapping for a consumer
 */
export function getCoBorrowerForConsumer(consumerId: string): CoBorrowerMapping | undefined {
  return CO_BORROWER_MAPPINGS.find((m) => m.primaryConsumerId === consumerId);
}

/**
 * Get all co-borrower mappings
 */
export function getAllCoBorrowerMappings(): CoBorrowerMapping[] {
  return CO_BORROWER_MAPPINGS;
}
